import { useEffect, useRef, useState } from 'react';
import InputError from '@/Components/InputError';
import { Head, useForm } from '@inertiajs/react';
import env from '@src/env';

export default function Login({ views, status, turnstileSiteKey }) {
	const { data, setData, post, processing, errors, reset } = useForm({
		username: '',
		password: '',
		redirect: views.redirect,
		remember: false,
		turnstile_token: '',
	});

	const [showPassword, setShowPassword] = useState(false);
	const [turnstileError, setTurnstileError] = useState('');
	const turnstileContainerRef = useRef(null);
	const turnstileWidgetIdRef = useRef(null);

	useEffect(() => {
		return () => {
			reset('password', 'turnstile_token');
		};
	}, [reset]);

	useEffect(() => {
		if (!turnstileSiteKey) {
			setTurnstileError('Captcha is not configured yet.');
			return undefined;
		}

		const renderTurnstile = () => {
			if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetIdRef.current !== null) {
				return;
			}

			setTurnstileError('');
			turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
				sitekey: turnstileSiteKey,
				theme: 'light',
				callback: (token) => {
					setTurnstileError('');
					setData('turnstile_token', token);
				},
				'expired-callback': () => {
					setData('turnstile_token', '');
					setTurnstileError('Captcha expired. Please verify again.');
				},
				'error-callback': () => {
					setData('turnstile_token', '');
					setTurnstileError('Captcha could not be verified. Please try again.');
				},
			});
		};

		if (window.turnstile) {
			renderTurnstile();
			return undefined;
		}

		const existingScript = document.querySelector('script[data-turnstile-script="true"]');
		const handleLoad = () => renderTurnstile();

		if (existingScript) {
			existingScript.addEventListener('load', handleLoad);
			return () => {
				existingScript.removeEventListener('load', handleLoad);
			};
		}

		const script = document.createElement('script');
		script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
		script.async = true;
		script.defer = true;
		script.dataset.turnstileScript = 'true';
		script.addEventListener('load', handleLoad);
		script.addEventListener('error', () => {
			setTurnstileError('Captcha failed to load. Please refresh the page.');
		});
		document.head.appendChild(script);

		return () => {
			script.removeEventListener('load', handleLoad);
		};
	}, [setData, turnstileSiteKey]);

	const resetTurnstile = () => {
		setData('turnstile_token', '');
		setTurnstileError('');

		if (window.turnstile && turnstileWidgetIdRef.current !== null) {
			window.turnstile.reset(turnstileWidgetIdRef.current);
		}
	};

	const submit = (e) => {
		e.preventDefault();

		post(route('web.auth.auth_login'), {
			onError: () => {
				resetTurnstile();
			},
		});
	};

	return (
		<div className="min-h-screen relative overflow-hidden">
			<Head title="Login" />

			<div
				className="absolute inset-0 z-0"
				style={{
					backgroundImage: `url('${env.REACT_URL}/image/bg_tga.png')`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			></div>

			<div className="relative z-20 min-h-screen flex items-center justify-start pl-8 md:pl-16 lg:pl-24">
				<div className="w-full max-w-sm">
					<div className="bg-white bg-opacity-90 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
						<div className="text-center mb-8">
							<h1 className="text-3xl font-bold text-black mb-2">LOGIN</h1>
							<div className="w-16 h-1 bg-black mx-auto"></div>
						</div>

						{status && (
							<div className="mb-4 font-medium text-sm text-green-600 text-center">
								{status}
							</div>
						)}

						<form onSubmit={submit} className="space-y-4">
							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
									</svg>
								</div>
								<input
									id="username"
									type="text"
									name="username"
									value={data.username}
									placeholder="Username"
									className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white"
									autoComplete="username"
									onChange={(e) => setData('username', e.target.value)}
									required
								/>
								<InputError message={errors.username} className="mt-1" />
							</div>

							<div className="relative">
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
									</svg>
								</div>
								<input
									id="password"
									type={showPassword ? 'text' : 'password'}
									name="password"
									value={data.password}
									placeholder="Enter password"
									className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white"
									autoComplete="current-password"
									onChange={(e) => setData('password', e.target.value)}
									required
								/>
								<button
									type="button"
									className="absolute inset-y-0 right-0 pr-3 flex items-center"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? (
										<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
										</svg>
									) : (
										<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
										</svg>
									)}
								</button>
								<InputError message={errors.password} className="mt-1" />
							</div>

							<div className="space-y-2">
								<div className="flex justify-center">
									<div ref={turnstileContainerRef}></div>
								</div>
								{turnstileError && (
									<p className="text-sm text-red-600 text-center">{turnstileError}</p>
								)}
								<InputError message={errors.turnstile_token} className="text-center" />
							</div>

							{!turnstileSiteKey && (
								<div className="text-sm text-red-600 text-center">
									Set <code>TURNSTILE_SITE_KEY</code> and <code>TURNSTILE_SECRET_KEY</code> in the env file to enable login.
								</div>
							)}

							<button
								type="submit"
								disabled={processing || !data.turnstile_token}
								className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed mt-6"
							>
								{processing ? 'Logging in...' : 'LOGIN'}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
